import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Mail, FileText, Search, CalendarClock, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/adminApi";
import { INDUSTRIA_LABELS } from "@/components/admin/LeadsManager";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("es-CL"); } catch { return iso || "-"; }
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return Math.floor((d - new Date()) / (1000 * 60 * 60 * 24));
};

export function ActivePoliciesManager({ leads, onChange }) {
  const [q, setQ] = useState("");
  const [sending, setSending] = useState({});

  const activePolicies = useMemo(() => {
    return (leads || [])
      .filter((l) => l.status === "ganado" && !l.archived && l.poliza)
      .sort((a, b) => (a.poliza_vence || "").localeCompare(b.poliza_vence || ""));
  }, [leads]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return activePolicies;
    return activePolicies.filter((l) => (
      [l.nombre, l.rut, l.email, l.aseguradora, l.equipo_descripcion, ...(l.tipo_industria || [])].join(" ").toLowerCase().includes(term)
    ));
  }, [activePolicies, q]);

  const sendPoliza = async (l) => {
    setSending((s) => ({ ...s, [l.id]: true }));
    try { await api.post(`/admin/leads/${l.id}/send-poliza`); toast.success("Póliza enviada"); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo"); }
    finally { setSending((s) => ({ ...s, [l.id]: false })); }
  };

  const expiring = activePolicies.filter((l) => {
    const d = daysUntil(l.poliza_vence);
    return d !== null && d <= 30;
  });

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-3xl font-bold text-brand-ink">Seguros Activos</h2>
        <p className="mt-1 text-sm text-gray-500">Clientes con póliza contratada. Revisa los próximos a vencer para renovarlos a tiempo.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <p className="mt-4 font-display text-3xl font-bold text-brand-ink">{activePolicies.length}</p>
          <p className="mt-1 text-sm text-gray-500">Pólizas activas</p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
            <CalendarClock className="h-5 w-5" />
          </div>
          <p className="mt-4 font-display text-3xl font-bold text-brand-ink">{expiring.length}</p>
          <p className="mt-1 text-sm text-gray-500">Vencen en 30 días</p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
            <Mail className="h-5 w-5" />
          </div>
          <p className="mt-4 font-display text-3xl font-bold text-brand-ink">{activePolicies.filter((l) => l.poliza_enviada_at).length}</p>
          <p className="mt-1 text-sm text-gray-500">Pólizas enviadas por correo</p>
        </div>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input data-testid="policies-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, RUT, aseguradora..." className="pl-9" />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <ShieldCheck className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          Sin pólizas activas.
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-2xl border border-gray-200 shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Industria</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Aseguradora</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Vencimiento</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Póliza</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((l) => {
                const d = daysUntil(l.poliza_vence);
                const soon = d !== null && d <= 30;
                const overdue = d !== null && d < 0;
                return (
                  <tr key={l.id} data-testid={`policy-row-${l.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-brand-ink">{l.nombre}</div>
                      <div className="text-xs text-gray-500">{l.rut} · {l.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(l.tipo_industria || []).map((k) => (
                          <span key={k} className="inline-flex items-center rounded-full bg-brand-yellow/15 text-brand-ink px-2 py-0.5 text-[10px] font-semibold">
                            {INDUSTRIA_LABELS[k] || k}
                          </span>
                        ))}
                      </div>
                      {l.equipo_descripcion && <div className="text-[11px] text-gray-500 mt-1 max-w-xs truncate" title={l.equipo_descripcion}>{l.equipo_descripcion}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{l.aseguradora || "-"}</td>
                    <td className="px-4 py-3">
                      <div className={`inline-flex items-center gap-1 text-xs font-semibold ${overdue ? "text-rose-600" : soon ? "text-amber-600" : "text-gray-700"}`}>
                        {overdue && <AlertTriangle className="h-3.5 w-3.5" />}
                        {l.poliza_vence ? fmtDate(l.poliza_vence) : "—"}
                        {d !== null && <span className="text-[10px] font-normal text-gray-500">({overdue ? `hace ${Math.abs(d)}d` : `en ${d}d`})</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/file?path=${encodeURIComponent(l.poliza.path)}`} target="_blank" rel="noreferrer" data-testid={`policy-view-${l.id}`} className="inline-flex items-center gap-1 text-xs text-brand-ink hover:text-brand-yellow">
                        <FileText className="h-3.5 w-3.5" /> {l.poliza.filename}
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="outline" onClick={() => sendPoliza(l)} disabled={!!sending[l.id]} data-testid={`policy-send-${l.id}`}>
                        {sending[l.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                        <span className="ml-1 text-xs">{l.poliza_enviada_at ? "Reenviar" : "Enviar"}</span>
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
